import { groupBy } from "lodash";
import {
  ISpiffApproval,
  EnrichedSpiffApproval,
  EnrichedSpiffApprovalUser,
} from "@/spiff-approvals/spiff-approval";
import { calculateSpiffCounts } from "@/spiffs/utils/calculate-spiff-counts";
import { DetailedUser } from "@/user/user";

// Enrich a single spiff approval with calculated counts and group spiffs by user
export const enrichSpiffApproval = (
  spiffApproval: ISpiffApproval,
  businessUsers: DetailedUser[] = [],
): EnrichedSpiffApproval | EnrichedSpiffApprovalUser => {
  const counts = calculateSpiffCounts(spiffApproval.spiffs);

  // Create a lookup map for business users
  const userLookup = businessUsers.reduce(
    (lookup, user) => {
      lookup[user.id] = user;
      return lookup;
    },
    {} as Record<string, DetailedUser>,
  );

  // Group spiffs by user_id
  const nonDismissedSpiffs = spiffApproval.spiffs.filter(
    (spiff) => !spiff.dismissed_at,
  );
  const spiffsByUser = groupBy(nonDismissedSpiffs, "user_id");

  // Create enriched user-based subrows
  const userSubrows = Object.entries(spiffsByUser).map(
    ([userId, userSpiffs]) => {
      const user = userLookup[userId]; // Get user from business users lookup
      const userCounts = calculateSpiffCounts(userSpiffs);

      // Create display name, fallback to "Deleted User" if user not found
      const displayName = user
        ? user.display_identification || user.email
        : "Deleted User";

      return {
        id: `${spiffApproval.id}-user-${userId}`,
        spiff_approval_id: spiffApproval.id,
        status: spiffApproval.status,
        total_amount_cents: Math.round(userCounts.total_amount * 100),
        spiffs: userSpiffs,
        created_at: spiffApproval.created_at,
        updated_at: spiffApproval.updated_at,
        effective_at: spiffApproval.effective_at,
        display_date: spiffApproval.display_date,
        pay_period_start_date: spiffApproval.pay_period_start_date,
        pay_period_inclusive_end_date:
          spiffApproval.pay_period_inclusive_end_date,
        ...userCounts,
        display_identification: displayName,
        user_id: userId, // Include user_id for sidebar functionality
      } as EnrichedSpiffApprovalUser;
    },
  );

  // If there's only one user, return the flattened user data
  // This avoids unnecessary grouping and shows the actual user name
  if (userSubrows.length === 1) {
    return userSubrows[0];
  }

  // For multiple users, return the grouped structure
  return {
    ...spiffApproval,
    ...counts,
    display_identification: `${userSubrows.length} users`,
    children: userSubrows,
  };
};
